import { useState, useEffect, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type {
  Case,
  ClientProfile,
  CasePriority,
  UpdateCaseInput,
} from '../../types/case-management'
import { EditCaseForm } from './EditCaseModal.parts'

interface EditCaseModalProps {
  isOpen: boolean
  caseData: Case | null
  clients: ClientProfile[]
  isUpdating?: boolean
  onSubmit?: (id: string, data: UpdateCaseInput) => void
  onClose?: () => void
}

export interface EditCaseFormState {
  name: string
  setName: (v: string) => void
  scopeDescription: string
  setScopeDescription: (v: string) => void
  selectedClientId: string | null
  setSelectedClientId: (v: string | null) => void
  orgCaseReference: string
  setOrgCaseReference: (v: string) => void
  priority: CasePriority
  setPriority: (v: CasePriority) => void
  showClientDropdown: boolean
  setShowClientDropdown: (v: boolean) => void
  clientSearch: string
  setClientSearch: (v: string) => void
  isValid: boolean
  handleSubmit: (e: React.FormEvent) => void
}

function buildUpdateInput(
  formData: {
    name: string
    scopeDescription: string
    priority: CasePriority
    orgCaseReference: string
    selectedClientId: string | null
  },
  caseData: Case
): UpdateCaseInput {
  const input: UpdateCaseInput = {}
  if (formData.name.trim() !== caseData.name) input.name = formData.name.trim()
  if (formData.scopeDescription.trim() !== caseData.scopeDescription)
    input.scopeDescription = formData.scopeDescription.trim()
  if (formData.priority !== caseData.priority) input.priority = formData.priority
  if (formData.orgCaseReference.trim() !== (caseData.orgCaseReference ?? '')) {
    const trimmedRef = formData.orgCaseReference.trim()
    if (trimmedRef) input.orgCaseReference = trimmedRef
  }
  if (formData.selectedClientId !== caseData.clientProfileId)
    input.clientProfileId = formData.selectedClientId
  return input
}

function useEditCaseForm(
  caseData: Case | null,
  onSubmit?: (id: string, data: UpdateCaseInput) => void
): EditCaseFormState {
  const [name, setName] = useState('')
  const [scopeDescription, setScopeDescription] = useState('')
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)
  const [orgCaseReference, setOrgCaseReference] = useState('')
  const [priority, setPriority] = useState<CasePriority>('medium')
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [clientSearch, setClientSearch] = useState('')

  useEffect(() => {
    if (caseData) {
      setName(caseData.name)
      setScopeDescription(caseData.scopeDescription)
      setSelectedClientId(caseData.clientProfileId ?? null)
      setOrgCaseReference(caseData.orgCaseReference ?? '')
      setPriority(caseData.priority)
    }
  }, [caseData])

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (!name.trim() || !scopeDescription.trim() || !caseData) return
    const input = buildUpdateInput(
      { name, scopeDescription, priority, orgCaseReference, selectedClientId },
      caseData
    )
    onSubmit?.(caseData.id, input)
  }

  return {
    name, setName, scopeDescription, setScopeDescription,
    selectedClientId, setSelectedClientId, orgCaseReference, setOrgCaseReference,
    priority, setPriority, showClientDropdown, setShowClientDropdown,
    clientSearch, setClientSearch,
    isValid: !!(name.trim() && scopeDescription.trim()),
    handleSubmit,
  }
}

export function EditCaseModal({
  isOpen,
  caseData,
  clients,
  isUpdating = false,
  onSubmit,
  onClose,
}: EditCaseModalProps): ReactElement | null {
  const { t } = useTranslation(['cases', 'common'])
  const form = useEditCaseForm(caseData, onSubmit)

  if (!isOpen || !caseData) return null

  const handleClientSelect = (id: string | null): void => {
    form.setSelectedClientId(id)
    form.setShowClientDropdown(false)
    form.setClientSearch('')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-hidden overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20 dark:border-slate-800 dark:bg-slate-900 dark:shadow-black/40">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4 dark:border-slate-800 dark:bg-slate-900">
          <div>
            <h2
              className="text-xl font-bold text-slate-900 dark:text-slate-100"
              style={{ fontFamily: 'var(--font-display, Syne, sans-serif)' }}
            >
              {t('editCase.title')}
            </h2>
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              {t('editCase.subtitle')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <EditCaseForm
          form={form}
          clients={clients}
          caseData={caseData}
          isUpdating={isUpdating}
          onClose={onClose}
          onClientSelect={handleClientSelect}
        />
      </div>
    </div>
  )
}
